import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminShowcases = () => {
  const [showcases, setShowcases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingShowcase, setEditingShowcase] = useState(null);
  const [formData, setFormData] = useState({
    type: 'upcoming',
    name: '',
    date: new Date().toISOString().split('T')[0],
    location: '',
    age_groups: '',
    description: ''
  });

  useEffect(() => {
    fetchShowcases();
  }, []);

  const fetchShowcases = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/showcases`);
      setShowcases(response.data);
    } catch (error) {
      console.error('Error fetching showcases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const showcaseData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        age_groups: formData.age_groups.split(',').map(g => g.trim())
      };
      
      if (editingShowcase) {
        await axios.put(`${API}/showcases/${editingShowcase.id}`, showcaseData);
      } else {
        await axios.post(`${API}/showcases`, showcaseData);
      }
      fetchShowcases();
      resetForm();
    } catch (error) {
      console.error('Error saving showcase:', error);
      alert('Error saving showcase. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this showcase?')) {
      try {
        await axios.delete(`${API}/showcases/${id}`);
        fetchShowcases();
      } catch (error) {
        console.error('Error deleting showcase:', error);
        alert('Error deleting showcase. Please try again.');
      }
    }
  };

  const handleEdit = (showcase) => {
    setEditingShowcase(showcase);
    setFormData({
      type: showcase.type,
      name: showcase.name,
      date: new Date(showcase.date).toISOString().split('T')[0],
      location: showcase.location,
      age_groups: showcase.age_groups.join(', '),
      description: showcase.description
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingShowcase(null);
    setFormData({
      type: 'upcoming',
      name: '',
      date: new Date().toISOString().split('T')[0],
      location: '',
      age_groups: '',
      description: ''
    });
  };

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={20} /> Back to Dashboard
              </Link>
              <h1 className="display-large">Manage Showcases</h1>
            </div>
            <button onClick={() => setShowForm(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} /> Add Showcase
            </button>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {showForm && (
            <div className="network-card" style={{ marginBottom: '32px' }}>
              <h2 className="heading-2">{editingShowcase ? 'Edit Showcase' : 'Add New Showcase'}</h2>
              <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  <div className="form-group">
                    <label>Type *</label>
                    <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} required>
                      <option value="upcoming">Upcoming</option>
                      <option value="past">Past</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Event Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required placeholder="e.g., Perfect Game Summer Showcase" />
                  </div>
                  <div className="form-group">
                    <label>Date *</label>
                    <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Location *</label>
                    <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required placeholder="e.g., San Diego, CA" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Age Groups (comma separated) *</label>
                    <input type="text" value={formData.age_groups} onChange={(e) => setFormData({...formData, age_groups: e.target.value})} required placeholder="e.g., 13u, 14u, 2026, 2027" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Description *</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required rows="3" placeholder="Brief description of the showcase" />
                  </div>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', gap: '16px' }}>
                  <button type="submit" className="btn-primary">{editingShowcase ? 'Update Showcase' : 'Add Showcase'}</button>
                  <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <p>Loading showcases...</p>
          ) : (
            <div className="network-card">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-medium)' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Location</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showcases.length === 0 ? (
                      <tr><td colSpan="5" style={{ padding: '24px', textAlign: 'center' }}>No showcases found</td></tr>
                    ) : (
                      showcases.map(showcase => (
                        <tr key={showcase.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '12px' }}>{showcase.name}</td>
                          <td style={{ padding: '12px' }}><span style={{ textTransform: 'capitalize' }}>{showcase.type}</span></td>
                          <td style={{ padding: '12px' }}>{new Date(showcase.date).toLocaleDateString()}</td>
                          <td style={{ padding: '12px' }}>{showcase.location}</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => handleEdit(showcase)} style={{ padding: '6px 12px', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Edit size={16} /> Edit
                              </button>
                              <button onClick={() => handleDelete(showcase.id)} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Trash2 size={16} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminShowcases;